import {
  Body1Stronger,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
  tokens,
} from '@fluentui/react-components';

import { APPROVAL_STATEMENTS, TApprovalUserDialogState } from '../types';
import { useUsers } from '@modules/users';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { toTimestampString } from '@shared/helpers';
import { useMemo } from 'react';
import resolveFunctionNames from '../helpers/resolveFunctionNames.helper';

type TProps = {
  state?: TApprovalUserDialogState;
  setState: (state?: TApprovalUserDialogState) => void;
  groupNameById: Map<string, string>;
};

const useStyles = makeStyles({
  dialog: {
    maxWidth: '700px',
  },
  dialogContent: {
    maxHeight: '400px',
    overflowY: 'auto',
  },
  PENDING: {},
  ACCEPTED: {
    color: tokens.colorBrandForeground1,
  },
  DECLINED: {
    color: tokens.colorStatusDangerForeground1,
  },
});

const ApprovalUserDialog = ({ state, setState, groupNameById }: TProps) => {
  const { userMap } = useUsers();

  const user = userMap && state ? userMap[state.approvalUser.user] : undefined;

  const functionNames = useMemo(
    () => resolveFunctionNames(state?.approvalUser.customGroupIds, groupNameById),
    [state?.approvalUser.customGroupIds, groupNameById]
  );

  const classes = useStyles();
  return (
    <Dialog
      open={Boolean(state?.isOpen && user)}
      onOpenChange={(_, data) => {
        if (state) setState({ ...state, isOpen: data.open });
      }}
    >
      <DialogSurface className={classes.dialog}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            {user?.name}
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {state && user && (
              <div className="flex flex-col space-y-2">
                <p className="flex items-center">
                  <span className="block w-32 flex-shrink-0">Status:</span>
                  <span className={classes[state.approvalUser.status]}>
                    <Body1Stronger>{state?.approvalUser.status}</Body1Stronger>
                  </span>
                </p>
                <p className="flex">
                  <span className="block w-32 flex-shrink-0">Approval role:</span>
                  {state.phaseName === 'AUTHORING'
                    ? 'Author'
                    : state.phaseName === 'REVIEWING'
                      ? 'Reviewer'
                      : 'Approver'}
                </p>
                {functionNames && (
                  <p className="flex">
                    <span className="block w-32 flex-shrink-0">Function:</span>
                    {functionNames}
                  </p>
                )}
                {user.mail && (
                  <p className="flex">
                    <span className="block w-32 flex-shrink-0">E-mail:</span>
                    {user.mail}
                  </p>
                )}
                {state.approvalUser.status === 'ACCEPTED' && (
                  <p className="flex">
                    <span className="block w-32 flex-shrink-0">Statement:</span>
                    {APPROVAL_STATEMENTS[state.phaseName]}
                  </p>
                )}

                {state.approvalUser.note && (
                  <span className="block p-2 border border-gray-300 rounded whitespace-pre-wrap">
                    {state.approvalUser.note}
                  </span>
                )}

                {state.approvalUser.status === 'ACCEPTED' && (
                  <div className="pt-2">
                    <div className="flex flex-col space-y-1 p-2 rounded-sm" style={{ backgroundColor: '#f2f2f2' }}>
                      <p className="">
                        <Body1Stronger>Electronically signed by Confideck GmbH</Body1Stronger>
                      </p>
                      {user.name && (
                        <p className="flex">
                          <span className="block w-32 flex-shrink-0">Full name:</span>
                          {user.name}
                        </p>
                      )}
                      <p className="flex">
                        <span className="block w-32 flex-shrink-0">User-ID:</span>
                        {user._id}
                      </p>
                      {user.msOid && (
                        <p className="flex">
                          <span className="block w-32 flex-shrink-0">Microsoft User-ID:</span>
                          {user.msOid}
                        </p>
                      )}
                      <p className="flex">
                        <span className="block w-32 flex-shrink-0">Timestamp:</span>
                        {toTimestampString(state.approvalUser.date)}
                      </p>
                      <p className="flex">
                        <span className="block w-32 flex-shrink-0">Authentication:</span>
                        Microsoft Entra ID
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ApprovalUserDialog;
